import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { BiEdit, BiTrash, BiShow } from "react-icons/bi";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  PlusIcon,
  DocumentArrowDownIcon,
  WrenchScrewdriverIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ArchiveBoxXMarkIcon,
} from "@heroicons/react/24/solid";
import {
  CubeIcon,
  BuildingOffice2Icon,
  DocumentTextIcon,
  AdjustmentsHorizontalIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { getAllAssets, createAsset, updateAsset, deleteAsset } from "../../Api/assets";

/* ─────────── helpers ─────────── */
const FILE_BASE_URL = "https://api2.eduforestin.com";

const getFileUrl = (value) =>
  value ? `${FILE_BASE_URL}/${value}` : "";

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const fmtCurrency = (n) =>
  n != null
    ? new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }).format(n)
    : "—";

/* ─────────── status config ─────────── */
const STATUS_CONFIG = {
  Active: {
    bg: "bg-green-100",
    text: "text-green-700",
    icon: <ShieldCheckIcon className="w-3.5 h-3.5" />,
    filterBg: "bg-green-50 border border-green-200",
    filterText: "text-green-700",
  },
  "In Repair": {
    bg: "bg-orange-100",
    text: "text-orange-700",
    icon: <WrenchScrewdriverIcon className="w-3.5 h-3.5" />,
    filterBg: "bg-orange-50 border border-orange-200",
    filterText: "text-orange-700",
  },
  Damaged: {
    bg: "bg-red-100",
    text: "text-red-700",
    icon: <ExclamationTriangleIcon className="w-3.5 h-3.5" />,
    filterBg: "bg-red-50 border border-red-200",
    filterText: "text-red-700",
  },
  Disposed: {
    bg: "bg-gray-200",
    text: "text-gray-600",
    icon: <ArchiveBoxXMarkIcon className="w-3.5 h-3.5" />,
    filterBg: "bg-gray-50 border border-gray-200",
    filterText: "text-gray-600",
  },
};

const ASSETTYPE_CONFIG = {
  DBSkills: {
    filterBg: "bg-indigo-50 border border-indigo-200",
    filterText: "text-indigo-700",
    color: "indigo",
  },
  Client: {
    filterBg: "bg-purple-50 border border-purple-200",
    filterText: "text-purple-700",
    color: "purple",
  },
};

const StatusBadge = ({ value, label }) => {
  const cfg = STATUS_CONFIG[value] ?? {
    bg: "bg-yellow-100",
    text: "text-yellow-700",
    icon: null,
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${cfg.bg} ${cfg.text}`}
    >
      {cfg.icon}
      {label || value || "—"}
    </span>
  );
};

/* ─────────── empty form ─────────── */
const toDateInputValue = (value) =>
  value ? new Date(value).toISOString().slice(0, 10) : "";

const emptyForm = {
  name: "",
  givenBy: "",
  givenDate: "",
  returnDate: "",
  location: "",
  assetType: "",
  clientName: "",
  brand: "",
  model: "",
  serialNumber: "",
  quantity: "",
  price: "",
  totalCost: "",
  vendor: "",
  purchaseDate: "",
  warrantyStart: "",
  warrantyEnd: "",
  status: "",
  bill: null,
};

const fieldLabels = {
  name: "Asset Name",
  givenBy: "Given By",
  givenDate: "Given Date",
  returnDate: "Return Date",
  location: "Location",
  assetType: "Asset Type",
  clientName: "Client Name",
  brand: "Brand",
  model: "Model",
  serialNumber: "Serial Number",
  quantity: "Quantity",
  price: "Unit Price (₹)",
  totalCost: "Total Cost (₹)",
  vendor: "Vendor",
  purchaseDate: "Purchase Date",
  warrantyStart: "Warranty Start",
  warrantyEnd: "Warranty End",
  status: "Status",
  bill: "Bill / Invoice",
};

const requiredFields = [
  "name",
  "givenBy",
  "givenDate",
  "location",
  "assetType",
  "quantity",
];

/* ═══════════════════════════════════════════════════════════ */
const AssetsManagement = () => {
  const [data, setData] = useState([]);
  const [loadingTable, setLoadingTable] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");

const [activeFilters, setActiveFilters] = useState({
  status: [],
  assetType: [],
  location: [],
  brand: [],
  assetName: [],
});

  const [openDrawer, setOpenDrawer] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  /* ─── load ─── */
  const loadAssets = async () => {
    try {
      setLoadingTable(true);
      const res = await getAllAssets();
      if (res.data.success) setData(res.data.data);
    } catch {
      toast.error("Failed to load assets");
    } finally {
      setLoadingTable(false);
    }
  };

  useEffect(() => {
    loadAssets();
  }, []);

  /* ─── compute filter options with counts ─── */
  const filterOptions = useMemo(() => {
    const statuses = {};
    const assetTypes = {};
    const locations = {};
    const brands = {};
const assetNames = {};
    data.forEach((item) => {
      // Count statuses
      if (item.status) {
        statuses[item.status] = (statuses[item.status] || 0) + 1;
      }
if (item.name) {
  assetNames[item.name] = (assetNames[item.name] || 0) + 1;
}
      // Count asset types
      if (item.assetType) {
        assetTypes[item.assetType] = (assetTypes[item.assetType] || 0) + 1;
      }

      // Count locations
      if (item.location) {
        locations[item.location] = (locations[item.location] || 0) + 1;
      }

      // Count brands
      if (item.brand) {
        brands[item.brand] = (brands[item.brand] || 0) + 1;
      }
    });

    return {
      statuses: Object.entries(statuses).sort((a, b) => b[1] - a[1]),
      assetTypes: Object.entries(assetTypes).sort((a, b) => b[1] - a[1]),
      locations: Object.entries(locations).sort((a, b) => b[1] - a[1]),
      brands: Object.entries(brands).sort((a, b) => b[1] - a[1]),
      assetNames: Object.entries(assetNames).sort((a, b) => b[1] - a[1]),
    };
  }, [data]);

  /* ─── filtered data ─── */
  const filtered = useMemo(() => {
    return data.filter((a) => {
      const q = search.toLowerCase();

      // Search filter
      const matchesSearch =
        a.name?.toLowerCase().includes(q) ||
        a.givenBy?.toLowerCase().includes(q) ||
        a.location?.toLowerCase().includes(q) ||
        a.brand?.toLowerCase().includes(q) ||
        a.serialNumber?.toLowerCase().includes(q);

      if (!matchesSearch) return false;

      // Status filter
      if (activeFilters.status.length > 0 && !activeFilters.status.includes(a.status)) {
        return false;
      }
if (
  activeFilters.assetName.length > 0 &&
  !activeFilters.assetName.includes(a.name)
) {
  return false;
}
      // Asset Type filter
      if (activeFilters.assetType.length > 0 && !activeFilters.assetType.includes(a.assetType)) {
        return false;
      }

      // Location filter
      if (activeFilters.location.length > 0 && !activeFilters.location.includes(a.location)) {
        return false;
      }

      // Brand filter
      if (activeFilters.brand.length > 0 && !activeFilters.brand.includes(a.brand)) {
        return false;
      }

      return true;
    });
  }, [data, search, activeFilters]);

  const paginated = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* ─── filter handlers ─── */
  const toggleFilter = (category, value) => {
    setActiveFilters((prev) => {
      const current = prev[category] || [];
      const updated = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [category]: updated };
    });
    setPage(0); // Reset to first page
  };

  const clearAllFilters = () => {
  setActiveFilters({
  status: [],
  assetType: [],
  location: [],
  brand: [],
  assetName: [],
});
    setSearch("");
    setPage(0);
  };

  const totalActiveFilters = Object.values(activeFilters).reduce(
    (sum, arr) => sum + arr.length,
    0
  );

  /* ─── form handlers ─── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "assetType" && value !== "Client" ? { clientName: "" } : {}),
    }));
  };

  const handleFile = (e) => {
    setForm((prev) => ({ ...prev, bill: e.target.files[0] }));
  };

  const openCreateDrawer = () => {
    setEditingAssetId(null);
    setForm(emptyForm);
    setOpenDrawer(true);
  };

  const closeDrawer = () => {
    setOpenDrawer(false);
    setEditingAssetId(null);
    setForm(emptyForm);
  };

  const handleEdit = (asset) => {
    setEditingAssetId(asset._id);
    setForm({
      name: asset.name || "",
      givenBy: asset.givenBy || "",
      givenDate: toDateInputValue(asset.givenDate),
      returnDate: toDateInputValue(asset.returnDate),
      location: asset.location || "",
      assetType: asset.assetType || "",
      clientName: asset.clientName || "",
      brand: asset.brand || "",
      model: asset.model || "",
      serialNumber: asset.serialNumber || "",
      quantity: asset.quantity ?? "",
      price: asset.price ?? "",
      totalCost: asset.totalCost ?? "",
      vendor: asset.vendor || "",
      purchaseDate: toDateInputValue(asset.purchaseDate),
      warrantyStart: toDateInputValue(asset.warrantyStart),
      warrantyEnd: toDateInputValue(asset.warrantyEnd),
      status: asset.status || "",
      bill: null,
    });
    setOpenDrawer(true);
  };

  const validateForm = () => {
    for (const key of requiredFields) {
      if (!form[key]?.toString().trim()) {
        toast.error(`Please fill ${fieldLabels[key]}`);
        return false;
      }
    }
    if (form.assetType === "Client" && !form.clientName?.trim()) {
      toast.error("Please fill Client Name");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true);
      toast.loading("Saving asset...", { id: "save-asset" });

      const fd = new FormData();
      Object.keys(form).forEach((key) => {
        if (form[key] !== null && form[key] !== "") fd.append(key, form[key]);
      });

      if (editingAssetId) {
        await updateAsset(editingAssetId, fd);
      } else {
        await createAsset(fd);
      }
      toast.success(editingAssetId ? "Asset updated" : "Asset created", {
        id: "save-asset",
      });
      closeDrawer();
      loadAssets();
    } catch {
      toast.error("Save failed", { id: "save-asset" });
    } finally {
      setSaving(false);
    }
  };

  /* ─── delete ─── */
  const askDelete = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const performDelete = async () => {
    try {
      await deleteAsset(deleteId);
      toast.success("Deleted");
      setDeleteDialogOpen(false);
      loadAssets();
    } catch {
      toast.error("Delete failed");
    }
  };

  /* ─────────────────────────────────── RENDER ─────────────────────────────────── */
  return (
    <Box p={3}>
  {/* Search Bar */}
<Box className="mb-3">
  <div className="relative w-full max-w-2xl">
    <div className="relative flex items-center h-12 rounded-xl bg-white border border-gray-200 shadow-sm">
      <span className="pl-4 text-gray-400">
        <MagnifyingGlassIcon className="w-5 h-5" />
      </span>
      <input
        type="text"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(0);
        }}
        placeholder="Search by Name, Type, Location, Brand..."
        className="flex-1 h-full bg-transparent px-3 text-sm text-gray-700 placeholder-gray-400 focus:outline-none"
      />
      {search && (
        <button
          onClick={() => {
            setSearch("");
            setPage(0);
          }}
          className="mr-3 w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  </div>
</Box>

{/* Stats Row */}
<Box className="mb-3 flex items-center justify-between gap-3 overflow-x-auto scrollbar-hide">
  <div className="flex items-center gap-3 shrink-0">
    <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 shadow-sm text-sm text-gray-600 font-medium">
      <CubeIcon className="w-4 h-4 text-indigo-500" />
      <span className="font-bold text-indigo-600">{data.length}</span>
      Total
    </div>

    <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 shadow-sm text-sm text-gray-600 font-medium">
      <ShieldCheckIcon className="w-4 h-4 text-green-500" />
      <span className="font-bold text-green-600">
        {data.filter((d) => d.status === "Active").length}
      </span>
      Active
    </div>

    <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 shadow-sm text-sm text-gray-600 font-medium">
      <WrenchScrewdriverIcon className="w-4 h-4 text-orange-500" />
      <span className="font-bold text-orange-600">
        {data.filter((d) => d.status === "In Repair").length}
      </span>
      In Repair
    </div>
  </div>

  <div className="flex items-center gap-3 shrink-0">
    <button
      disabled
      className="flex items-center gap-2 h-10 px-4 rounded-xl border border-gray-300 bg-gray-100 text-gray-400 font-medium shadow-sm cursor-not-allowed whitespace-nowrap"
    >
      <DocumentArrowDownIcon className="w-5 h-5" />
      Export
    </button>

    <button
      onClick={openCreateDrawer}
      className="flex items-center gap-2 h-10 px-5 rounded-xl bg-emerald-600 text-white font-semibold shadow-sm hover:bg-emerald-700 transition whitespace-nowrap"
    >
      <PlusIcon className="w-5 h-5" />
      Add Asset
    </button>
  </div>
</Box>

{/* Filters Card */}
<Box className="mb-3 rounded-2xl bg-white border border-gray-200 shadow-sm p-4">
  <div className="flex items-center justify-between gap-3 mb-3">
    <div className="flex items-center gap-2">
      <AdjustmentsHorizontalIcon className="w-5 h-5 text-gray-500" />
      <p className="text-sm font-bold text-gray-800">Filters</p>
      {totalActiveFilters > 0 && (
        <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-bold">
          {totalActiveFilters} active
        </span>
      )}
    </div>

    {(totalActiveFilters > 0 || search) && (
      <button
        onClick={clearAllFilters}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs font-bold hover:bg-red-100 transition whitespace-nowrap"
      >
        <XMarkIcon className="w-4 h-4" />
        Clear All Filters
      </button>
    )}
  </div>

  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
    <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-3">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-gray-500">
        Asset Name
      </p>
      <div className="flex flex-wrap gap-1.5">
        {filterOptions.assetNames.slice(0, 15).map(([name, count]) => {
          const isActive = activeFilters.assetName.includes(name);

          return (
            <button
              key={name}
              onClick={() => toggleFilter("assetName", name)}
              className={`max-w-full inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition ${
                isActive
                  ? "bg-indigo-50 border-indigo-300 text-indigo-700 shadow-sm"
                  : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
              }`}
            >
              <span className="truncate">{name}</span>
              <span
                className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  isActive
                    ? "bg-indigo-100 text-indigo-700"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>
    </div>

    <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-3">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-gray-500">
        Status
      </p>
      <div className="flex flex-wrap gap-1.5">
        {filterOptions.statuses.map(([status, count]) => {
          const cfg = STATUS_CONFIG[status];
          const isActive = activeFilters.status.includes(status);

          return (
            <button
              key={status}
              onClick={() => toggleFilter("status", status)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition ${
                isActive
                  ? `${cfg?.filterBg || "bg-yellow-50 border-yellow-300"} ${
                      cfg?.filterText || "text-yellow-700"
                    } shadow-sm`
                  : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
              }`}
            >
              {cfg?.icon}
              <span>{status}</span>
              <span
                className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  isActive
                    ? `${cfg?.bg || "bg-yellow-100"} ${
                        cfg?.text || "text-yellow-700"
                      }`
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>
    </div>

    <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-3">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-gray-500">
        Asset Type
      </p>
      <div className="flex flex-wrap gap-1.5">
        {filterOptions.assetTypes.map(([type, count]) => {
          const isActive = activeFilters.assetType.includes(type);

          return (
            <button
              key={type}
              onClick={() => toggleFilter("assetType", type)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition ${
                isActive
                  ? type === "Client"
                    ? "bg-purple-50 border-purple-300 text-purple-700 shadow-sm"
                    : "bg-indigo-50 border-indigo-300 text-indigo-700 shadow-sm"
                  : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
              }`}
            >
              <span>{type}</span>
              <span
                className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  isActive
                    ? type === "Client"
                      ? "bg-purple-100 text-purple-700"
                      : "bg-indigo-100 text-indigo-700"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>
    </div>

    <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-3">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-gray-500">
        Location
      </p>
      <div className="flex flex-wrap gap-1.5">
        {filterOptions.locations.slice(0, 8).map(([location, count]) => {
          const isActive = activeFilters.location.includes(location);

          return (
            <button
              key={location}
              onClick={() => toggleFilter("location", location)}
              className={`max-w-full inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition ${
                isActive
                  ? "bg-cyan-50 border-cyan-300 text-cyan-700 shadow-sm"
                  : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
              }`}
            >
              <span className="truncate">{location}</span>
              <span
                className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  isActive
                    ? "bg-cyan-100 text-cyan-700"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>
    </div>

    <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-3">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-gray-500">
        Brand
      </p>
      <div className="flex flex-wrap gap-1.5">
        {filterOptions.brands.slice(0, 12).map(([brand, count]) => {
          const isActive = activeFilters.brand.includes(brand);

          return (
            <button
              key={brand}
              onClick={() => toggleFilter("brand", brand)}
              className={`max-w-full inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition ${
                isActive
                  ? "bg-rose-50 border-rose-300 text-rose-700 shadow-sm"
                  : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
              }`}
            >
              <span className="truncate">{brand}</span>
              <span
                className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  isActive
                    ? "bg-rose-100 text-rose-700"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  </div>
</Box>

{/* Results Count */}
<Box className="mb-4 flex items-center justify-between">
  <Typography variant="body2" className="text-gray-600">
    <span className="font-semibold text-gray-800">{filtered.length}</span> assets match
    {totalActiveFilters > 0 && (
      <span> filtered from {data.length} total</span>
    )}
  </Typography>
</Box>

      {/* ══════════ TABLE ══════════ */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "16px",
          boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
          overflowX: "auto",
        }}
      >
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow
              sx={{ background: "linear-gradient(to right, #eef2ff, #e0e7ff)" }}
            >
              <TableCell>#</TableCell>
              <TableCell>Asset</TableCell>
              <TableCell>Given By</TableCell>
              <TableCell>Given Date</TableCell>
              <TableCell>Return Date</TableCell>
              <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                Location
              </TableCell>
              <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>
                Asset Type
              </TableCell>
              <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                Qty / Cost
              </TableCell>
              <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>
                Purchase Date
              </TableCell>
              <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>
                Warranty
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loadingTable ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : paginated.length ? (
              paginated.map((a, idx) => (
                <TableRow key={a._id} hover>
                  {/* # */}
                  <TableCell>
                    <strong>{page * rowsPerPage + idx + 1}</strong>
                  </TableCell>

                  {/* Asset */}
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <div className="w-10 h-10 rounded-full bg-indigo-200 flex items-center justify-center shadow shrink-0">
                        <span className="text-indigo-700 font-bold">
                          {a.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold">{a.name}</p>
                        <p className="text-xs text-gray-500 sm:hidden">{a.type}</p>
                        {a.serialNumber && (
                          <p className="text-xs text-gray-400">
                            S/N: {a.serialNumber}
                          </p>
                        )}
                      </div>
                    </Box>
                  </TableCell>

                  {/* Type / Brand */}
                  <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                    <p className="font-medium">{a.givenBy}</p>
                    {a.brand && (
                      <p className="text-xs text-gray-500">
                        {a.brand} {a.model ? `· ${a.model}` : ""}
                      </p>
                    )}
                  </TableCell>

                  <TableCell>
                    {fmtDate(a.givenDate)}
                  </TableCell>

                  <TableCell>
                    {a.returnDate ? fmtDate(a.returnDate) : "Not Returned"}
                  </TableCell>

                  {/* Location */}
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <BuildingOffice2Icon className="w-4 h-4 text-indigo-400 shrink-0" />
                      {a.location || "—"}
                    </Box>
                  </TableCell>

                  {/* Asset Type */}
                  <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>
                    {a.assetType === "Client"
                      ? `Client (${a.clientName || "—"})`
                      : a.assetType || "DBSkills"}
                  </TableCell>

                  {/* Qty / Cost */}
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    <p className="font-semibold">{a.quantity}</p>
                    <p className="text-xs text-gray-500">{fmtCurrency(a.totalCost)}</p>
                  </TableCell>

                  {/* Purchase Date */}
                  <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>
                    {fmtDate(a.purchaseDate)}
                  </TableCell>

                  {/* Warranty */}
                  <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>
                    <StatusBadge value={a.warrantyStatus} />
                    {a.warrantyEnd && (
                      <p className="text-xs text-gray-400 mt-1">
                        Till {fmtDate(a.warrantyEnd)}
                      </p>
                    )}
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <StatusBadge value={a.status} />
                  </TableCell>

                  {/* Actions */}
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <button
                        onClick={() => {
                          setSelectedAsset(a);
                          setViewDialogOpen(true);
                        }}
                        className="p-2 rounded-lg bg-white shadow hover:bg-gray-100 transition"
                        title="View"
                      >
                        <BiShow size={18} className="text-blue-600" />
                      </button>
                      <button
                        onClick={() => handleEdit(a)}
                        className="p-2 rounded-lg bg-white shadow hover:bg-yellow-100 transition"
                        title="Edit"
                      >
                        <BiEdit size={18} className="text-yellow-600" />
                      </button>
                      <button
                        onClick={() => askDelete(a._id)}
                        className="p-2 rounded-lg bg-white shadow hover:bg-red-100 transition"
                        title="Delete"
                      >
                        <BiTrash size={18} className="text-red-600" />
                      </button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Box py={6} textAlign="center">
                    <CubeIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <Typography color="text.secondary">
                      {totalActiveFilters > 0 || search
                        ? "No assets match your filters"
                        : "No assets found"}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </TableContainer>

      {/* ══════════ CREATE DRAWER ══════════ */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "80%", md: "60%", lg: "50%", xl: "55%" },
            backgroundColor: "#ffffff",
            p: 0,
          },
        }}
      >
        <Box
          sx={{
            p: 4,
            maxHeight: "100vh",
            overflowY: "auto",
            background: "#f9fafb",
          }}
        >
          {/* Header */}
          <Typography variant="h5" fontWeight="bold" mb={3} color="gray.800">
            {editingAssetId ? "Edit Asset" : "Add New Asset"}
          </Typography>

          <Box
            sx={{
              p: 3,
              borderRadius: 3,
            }}
          >
            {/* ─── Section: Identity ─── */}
            <p className="text-xs font-bold uppercase tracking-widest text-indigo-500 mb-3">
              Asset Identity
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 mb-6">
              {[
                "name",
                "givenBy",
                "brand",
                "model",
                "serialNumber",
                "location"
              ].map((f) => (
                <div key={f} className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-1">
                    {fieldLabels[f]}
                    {requiredFields.includes(f) && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                  </label>
                  <input
                    name={f}
                    value={form[f]}
                    onChange={handleChange}
                    placeholder={fieldLabels[f]}
                    className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  />
                </div>
              ))}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1">
                  Given Date *
                </label>
                <input
                  type="date"
                  name="givenDate"
                  value={form.givenDate}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1">
                  Return Date
                </label>
                <input
                  type="date"
                  name="returnDate"
                  value={form.returnDate}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1">
                  {fieldLabels.assetType}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <select
                  name="assetType"
                  value={form.assetType}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                >
                  <option value="">Select</option>
                  <option value="DBSkills">DBSkills</option>
                  <option value="Client">Client</option>
                </select>
              </div>

              {form.assetType === "Client" && (
                <div className="flex flex-col">
                  <label className="text-sm font-semibold text-gray-700 mb-1">
                    {fieldLabels.clientName}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    name="clientName"
                    value={form.clientName}
                    onChange={handleChange}
                    placeholder={fieldLabels.clientName}
                    className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  />
                </div>
              )}
            </div>

            {/* ─── Section: Financials ─── */}
            <p className="text-xs font-bold uppercase tracking-widest text-emerald-500 mb-3">
              Financials & Procurement
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 mb-6">
              {/* Quantity */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1">
                  {fieldLabels.quantity}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="number"
                  name="quantity"
                  value={form.quantity}
                  onChange={handleChange}
                  min={1}
                  className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              {/* Price */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1">
                  {fieldLabels.price}
                </label>
                <input
                  type="number"
                  name="price"
                  value={form.price}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              {/* Total Cost */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1">
                  {fieldLabels.totalCost}
                </label>
                <input
                  type="number"
                  name="totalCost"
                  value={form.totalCost}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              {/* Vendor */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1">
                  {fieldLabels.vendor}
                </label>
                <input
                  name="vendor"
                  value={form.vendor}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              {/* Purchase Date */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1">
                  {fieldLabels.purchaseDate}
                </label>
                <input
                  type="date"
                  name="purchaseDate"
                  value={form.purchaseDate}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
            </div>

            {/* ─── Section: Warranty & Status ─── */}
            <p className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-3">
              Warranty & Status
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 mb-6">
              {/* Warranty Start */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1">
                  {fieldLabels.warrantyStart}
                </label>
                <input
                  type="date"
                  name="warrantyStart"
                  value={form.warrantyStart}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              {/* Warranty End */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1">
                  {fieldLabels.warrantyEnd}
                </label>
                <input
                  type="date"
                  name="warrantyEnd"
                  value={form.warrantyEnd}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              {/* Status */}
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-gray-700 mb-1">
                  {fieldLabels.status}
                </label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                >
                  <option value="">Select Status</option>
                  <option>Active</option>
                  <option>In Repair</option>
                  <option>Damaged</option>
                  <option>Disposed</option>
                </select>
              </div>
            </div>

            {/* ─── Section: Document ─── */}
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">
              Documents
            </p>
            <div className="flex flex-col mb-6">
              <label className="text-sm font-semibold text-gray-700 mb-1">
                Upload Bill / Invoice
              </label>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={handleFile}
                className="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              {form.bill && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ {form.bill.name}
                </p>
              )}
            </div>

            {/* Action buttons */}
            <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={closeDrawer}
                sx={{ textTransform: "none", px: 4 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={saving}
                sx={{ textTransform: "none", px: 4 }}
              >
                {saving ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Saving...
                  </>
                ) : (
                  editingAssetId ? "Update Asset" : "Save Asset"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* ══════════ VIEW DIALOG ══════════ */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>Asset Details</DialogTitle>
        <DialogContent dividers>
          {selectedAsset && (
            <>
              {/* Status row */}
              <Box mb={3} display="flex" gap={2} flexWrap="wrap">
                <StatusBadge value={selectedAsset.status} />
                <StatusBadge
                  value={selectedAsset.warrantyStatus}
                  label={`Warranty: ${selectedAsset.warrantyStatus}`}
                />
              </Box>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.keys(emptyForm)
                  .filter((f) => f !== "bill")
                  .map((f) => (
                    <div key={f}>
                      <p className="text-sm text-gray-500">{fieldLabels[f]}</p>
                      <p className="font-semibold text-gray-800">
                        {["price", "totalCost"].includes(f)
                          ? fmtCurrency(selectedAsset[f])
                          : ["purchaseDate", "warrantyStart", "warrantyEnd"].includes(f)
                          ? fmtDate(selectedAsset[f])
                          : selectedAsset[f] || "—"}
                      </p>
                    </div>
                  ))}

                {/* Bill / Invoice */}
                <div className="col-span-2 md:col-span-3">
                  <p className="text-sm text-gray-500">Bill / Invoice</p>
                  {selectedAsset.bill ? (
                    <Box mt={1} display="flex" flexDirection="column" gap={1}>
                      <a
                        href={getFileUrl(selectedAsset.bill)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-indigo-600 underline text-sm"
                      >
                        <DocumentTextIcon className="w-4 h-4" />
                        Open File
                      </a>
                      {/\.(jpg|jpeg|png|gif)$/i.test(selectedAsset.bill) && (
                        <img
                          src={getFileUrl(selectedAsset.bill)}
                          alt="bill preview"
                          className="w-48 h-auto rounded border mt-1"
                        />
                      )}
                    </Box>
                  ) : (
                    <p className="text-gray-400">—</p>
                  )}
                </div>

                {/* Timestamps */}
                <div>
                  <p className="text-sm text-gray-500">Created At</p>
                  <p className="font-semibold text-gray-800">
                    {selectedAsset.createdAt
                      ? new Date(selectedAsset.createdAt).toLocaleString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })
                      : "—"}
                  </p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ══════════ DELETE CONFIRM ══════════ */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Delete Asset?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={performDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssetsManagement;